import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Helmet } from 'react-helmet-async';
import DeleteAccountDialog from '../components/DeleteAccountDialog';

const Profile: React.FC = () => {
  const { user, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  if (!user) {
    return (
      <div className="min-h-screen pt-20 bg-background-light">
        <div className="container-custom">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <h2 className="text-2xl font-heading font-bold text-secondary-dark mb-4">
                Loading...
              </h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    const { error } = await deleteAccount();

    if (error) {
      setDeleteError(error.message || 'Failed to delete account. Please try again.');
      setIsDeleting(false);
      return;
    }

    // Account deleted — redirect to home
    setIsDeleting(false);
    setIsDialogOpen(false);
    navigate('/');
  };

  return (
    <>
      <Helmet>
        <title>Profile - Next in Cinema</title>
        <meta name="description" content="Your profile on Next in Cinema" />
      </Helmet>

      <div className="min-h-screen pt-20 bg-background-light">
        <div className="container-custom py-8">
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Main profile card */}
            <div className="card p-8">
              <div className="text-center mb-8">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-primary/10 bg-primary flex items-center justify-center text-white text-4xl font-bold">
                  {user.email?.charAt(0).toUpperCase()}
                </div>
                <h1 className="text-3xl font-heading font-bold text-secondary-dark mb-2">
                  Welcome!
                </h1>
                <p className="text-secondary">
                  {user.email}
                </p>
              </div>

              <div className="space-y-6">
                <div className="border-t border-secondary/10 pt-6">
                  <h2 className="text-xl font-heading font-semibold text-secondary-dark mb-4">
                    Account Information
                  </h2>

                  <div className="grid gap-4">
                    <div className="flex justify-between items-center py-3 border-b border-secondary/5">
                      <span className="font-medium text-secondary-dark">Email</span>
                      <span className="text-secondary">{user.email}</span>
                    </div>

                    <div className="flex justify-between items-center py-3 border-b border-secondary/5">
                      <span className="font-medium text-secondary-dark">User ID</span>
                      <span className="text-secondary text-sm">{user.id}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t border-secondary/10 pt-6">
                  <h2 className="text-xl font-heading font-semibold text-secondary-dark mb-4">
                    Your Activity
                  </h2>
                  <div className="bg-background-light rounded-lg p-6 text-center">
                    <p className="text-secondary">
                      Welcome to Next in Cinema! Start exploring upcoming movies and building your watchlist.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Danger Zone card */}
            <div className="card p-6 border border-red-200">
              <h2 className="text-lg font-heading font-semibold text-red-600 mb-1 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                Danger Zone
              </h2>
              <p className="text-secondary text-sm mb-4">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>

              {deleteError && (
                <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {deleteError}
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setDeleteError(null);
                  setIsDialogOpen(true);
                }}
                className="px-4 py-2 rounded-lg border border-red-300 text-red-600 font-medium text-sm hover:bg-red-50 transition-colors"
              >
                Delete my account
              </button>
            </div>
          </div>
        </div>
      </div>

      <DeleteAccountDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleDeleteAccount}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default Profile;
